import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useControlTypeLabel } from 'src/pages/assessments/forms/useControlTypeLabel';

import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';

import { decorateWithControlType, getParentTitle } from './helpers';
import type {
  SecondLineResultFields,
  SecondLineResultRegisterFields,
} from './types';

export const useLabelledFields = (
  records: SecondLineResultFields[] | undefined,
  configIds: string[] = []
) => {
  const { t: at } = useTranslation('common', {
    keyPrefix: 'assessmentResults',
  });
  const assessmentResultTypes = at('resultTypes', { returnObjects: true });
  const getControlTypeLabel = useControlTypeLabel();
  const { resolveRiskRating } = useRiskRatingResolver('standard', {
    configType: 'compliance_monitoring',
    configIds,
  });
  const { getByValue: getByResultValue } = useRating('performance_result');
  const { getByValue: getEffectivenessByValue } = useRating('effectiveness');
  const labelledFields = useMemo<
    SecondLineResultRegisterFields[] | undefined
  >(() => {
    const ratingFns = {
      document_second_line_result: (d: SecondLineResultFields) =>
        getByResultValue(d.Rating),
      obligation_second_line_result: (d: SecondLineResultFields) =>
        getByResultValue(d.Rating),
      control_test_second_line_result: (d: SecondLineResultFields) =>
        getEffectivenessByValue(d.OverallEffectiveness),
      risk_uncontrolled_second_line_result: (d: SecondLineResultFields) =>
        resolveRiskRating({
          likelihood: d.Likelihood,
          impact: d.Impact,
          controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
          rating: d.Rating,
          configId: d.ConfigId,
        }),

      risk_controlled_second_line_result: (d: SecondLineResultFields) =>
        resolveRiskRating({
          likelihood: d.Likelihood,
          impact: d.Impact,
          controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
          rating: d.Rating,
          configId: d.ConfigId,
        }),
    };

    return records?.map((d) => {
      return {
        ...d,
        TypeLabelled: decorateWithControlType(
          assessmentResultTypes[
            d.typename as keyof typeof assessmentResultTypes
          ],
          getControlTypeLabel,
          d
        ),
        ParentTitle: getParentTitle(d) || '-',
        RatingLabelled:
          ratingFns[d.typename as keyof typeof ratingFns](d)?.label || '-',
        Rationale: d.Rationale,
        TestDate: d.TestDate,
      };
    });
  }, [
    records,
    assessmentResultTypes,
    getByResultValue,
    getControlTypeLabel,
    resolveRiskRating,
    getEffectivenessByValue,
  ]);

  return labelledFields;
};
